import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { FileText, Inbox, Send, Clock, CheckCheck } from "lucide-react";

const STATUS_PILL = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  rfq_generated: { label: "RFQ ready", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  order_pending: { label: "Order pending", cls: "bg-amber-50 text-amber-800 border-amber-200" },
};

function status(r) {
  const s = STATUS_PILL[r.status] || { label: r.status.replace(/_/g,' '), cls: "bg-slate-100 text-slate-600 border-slate-200" };
  return <span className={`pill ${s.cls}`}>{s.label}</span>;
}

export default function MyRequests() {
  const [items, setItems] = useState([]);
  const [rfqMap, setRfqMap] = useState({});
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    Promise.all([api.get("/sourcing/requests"), api.get("/rfqs")])
      .then(([a, b]) => {
        setItems(a.data);
        const m = {};
        for (const r of b.data) m[r.request_id] = r;
        setRfqMap(m);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow mb-2">All requests</div>
        <h1 className="page-title">My sourcing requests</h1>
        <p className="text-sm text-slate-500 mt-1">Every request you created, with the current RFQ state and verified communication activity.</p>
      </div>

      <div className="surface">
        {loading ? (
          <div className="p-5 space-y-3">{[...Array(4)].map((_,i)=>(<div key={i} className="skeleton h-16"/>))}</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            <FileText className="mx-auto text-slate-300 mb-3" size={32}/>
            <div className="mb-4">No requests yet.</div>
            <Link to="/buyer/new" className="btn-gold inline-flex">Create first request</Link>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((r) => {
              const rfq = rfqMap[r.id];
              return (
                <li key={r.id}>
                  <Link to={`/buyer/requests/${r.id}`} className="block px-5 py-4 hover:bg-slate-50 transition-colors" data-testid={`req-${r.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="font-semibold text-[15px] tracking-tight truncate">{r.requirements?.product_or_service || "Untitled request"}</div>
                          {status(r)}
                        </div>
                        <div className="text-xs text-slate-500 mt-1 line-clamp-1">{r.original_text}</div>
                        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500">
                          <span className="font-mono">{r.id.slice(0,8)}</span>
                          <span>{new Date(r.created_at).toLocaleDateString(undefined, {year:"numeric", month:"short", day:"numeric"})}</span>
                          {r.requirements?.destination && <span>→ {r.requirements.destination}</span>}
                        </div>
                      </div>
                      {rfq && (
                        <div className="flex items-center gap-3 text-[11px] text-slate-500 shrink-0">
                          <span className="inline-flex items-center gap-1" title="Suppliers RFQ dispatched to"><Send size={11}/>{rfq.sent_to_supplier_ids?.length || 0}</span>
                          <span className="inline-flex items-center gap-1" title="Suppliers that opened the RFQ"><Clock size={11}/>{rfq.viewed_by_supplier_ids?.length || 0}</span>
                          <span className="inline-flex items-center gap-1" title="Quotations received"><Inbox size={11}/>{rfq.responded_by_supplier_ids?.length || 0}</span>
                          {rfq.status === "closed" && <span className="inline-flex items-center gap-1 text-amber-700"><CheckCheck size={11}/>closed</span>}
                        </div>
                      )}
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
